import { getBarStyle } from './bar-style.js';
import { BarCanvas } from '../../core/components/canvas/components/BarCanvas.js';
import { useGridPlotSize } from '../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { safeGet } from '../../core/utils/safe-get.js';
import { useCategoricalBarChartBaseConfig } from '../hooks/useCategoricalBarChartBaseConfig.js';
import { useCategoricalBarFilteredData } from '../hooks/useCategoricalBarFilteredData.js';
import { useDisplayConfigContext } from '../hooks/useDisplayConfigContext.js';
import { Dimension } from '../types/categorical-bar-chart-data.js';
import { getBarCoordinates } from '../utils/categorical-layout.js';
import { isVertical } from '../utils/layout.js';

export const BarChartDisplay = () => {
  const { highlightedDimensions, categoryScale, dimensionScale, valueScale } =
    useDisplayConfigContext();
  const { filteredCategories, isSingleDimension, dimensionMap, categoryMap } =
    useCategoricalBarFilteredData();
  const { layout, groupMode } = useCategoricalBarChartBaseConfig();
  const { width, height } = useGridPlotSize();

  const vertical = isVertical(layout);
  const { categoryCoord, valueCoord, barSize, barThickness } =
    getBarCoordinates(
      groupMode,
      categoryScale,
      valueScale,
      dimensionScale,
      isSingleDimension,
      dimensionMap,
      categoryMap,
    );

  const widthAccessor = vertical ? barThickness : barSize;
  const heightAccessor = vertical ? barSize : barThickness;

  const color = (dimension: Dimension) => {
    const { name, color } = safeGet(dimensionMap, dimension);

    return getBarStyle(name, color, highlightedDimensions).color;
  };

  const opacity = (dimension: Dimension) => {
    const { name, color } = safeGet(dimensionMap, dimension);

    return getBarStyle(name, color, highlightedDimensions).opacity;
  };

  return (
    <div data-testid="bar-chart-display">
      {filteredCategories.map((category) => {
        const y = vertical
          ? valueCoord
          : (d: Dimension) => categoryCoord(d, category);
        const x = vertical
          ? (d: Dimension) => categoryCoord(d, category)
          : valueCoord;

        return (
          <BarCanvas
            key={category.id}
            data={category.dimensions}
            canvasWidth={width}
            canvasHeight={height}
            x={x}
            y={y}
            width={widthAccessor}
            height={heightAccessor}
            isVertical={vertical}
            strokeWidth={1}
            color={color}
            opacity={opacity}
          />
        );
      })}
    </div>
  );
};

BarChartDisplay['displayName'] = 'BarChartDisplay';
